

import { AdminUserService } from '@/_api/services/adminUser';
import { AlertService } from '@/_api/services/alert';
import { AuditService } from '@/_api/services/audit';
import { AuthService } from '@/_api/services/auth';
import { EmployeeService } from '@/_api/services/employee';
import { ResourceTypeService } from '@/_api/services/resourceType';
import { RosterService } from '@/_api/services/roster';
import { ServiceService } from '@/_api/services/service';
import { ServiceAssociationService } from '@/_api/services/serviceAssociation';
import { ServiceAutomationService } from '@/_api/services/serviceAutomation';
import { ServiceCategoryService } from '@/_api/services/serviceCategory';
import { ServiceFormService } from '@/_api/services/serviceForm';
import { ServiceResourceService } from '@/_api/services/serviceResource';
import { CustomerService } from '@/_api/services/customer';
import { ReservationService } from '@/_api/services/reservation';
import { OrderService } from '@/_api/services/order';
import { BookingService } from '@/_api/services/booking';
import { ReservationCustomerService } from '@/_api/services/reservationCustomer';
import { CustomerPassService } from '@/_api/services/customerPass';
import { CustomerMessageService } from '@/_api/services/customerMessage';
import { CustomerFormService } from '@/_api/services/customerForm';
import { CustomerNoteService } from '@/_api/services/customerNote';
import { ProductService } from '@/_api/services/product';
import { BillingService } from '@/_api/services/billing';
import { IntegrationService } from '@/_api/services/integration';
import { SettingsService } from '@/_api/services/settings';
import { PurchaseOrderService } from '@/_api/services/purchaseOrder';
import { CourseService } from '@/_api/services/course';
import { CustomerCourseService } from '@/_api/services/customerCourse';
import { CampaignService } from '@/_api/services/campaign';
import { FormService } from '@/_api/services/form';
import { WaitlistService } from '@/_api/services/waitlist';
import { ContentTemplateService } from '@/_api/services/contentTemplate';
import { MinisiteService } from '@/_api/services/minisite';
import { MarketingService } from '@/_api/services/marketing';
import { LocationService } from '@/_api/services/location';
import { EmailTemplateService } from '@/_api/services/emailTemplate';
import { ReportService } from '@/_api/services/report';
import { UpdateService } from '@/_api/services/update';
import { ReviewService } from '@/_api/services/review';
import { DemoService } from '@/_api/services/demo';
import { ExportService } from '@/_api/services/export';
import { ReferralService } from '@/_api/services/referral';
import { CustomPageService } from '@/_api/services/customPage';
import { CalendarFeedService } from '@/_api/services/calendarFeed';
import { StockTakeService } from '@/_api/services/stockTake';
import { FrontDeskService } from '@/_api/services/frontDesk';
import { CustomRoleService } from '@/_api/services/customRole';
import { AdvancedSettingsService } from '@/_api/services/advancedSettings';
import { ChargeModelService } from '@/_api/services/chargeModel';
import { CustomFieldService } from '@/_api/services/customField';
import { DiscountModelService } from '@/_api/services/discountModel';
import { GiftCardService } from '@/_api/services/giftCard';
import { PassService } from '@/_api/services/pass';
import { PaymentModelService } from '@/_api/services/paymentModel';
import { ProductBrandService } from '@/_api/services/productBrand';
import { ProductCategoryService } from '@/_api/services/productCategory';
import { ReservationCancellationTypeService } from '@/_api/services/reservationCancellationType';
import { ReservationStatusTypeService } from '@/_api/services/reservationStatusType';
import { RoleService } from '@/_api/services/role';
import { SegmentService } from '@/_api/services/segment';
import { SupplierService } from '@/_api/services/supplier';
import { TaxService } from '@/_api/services/tax';
import { TerminologyService } from '@/_api/services/terminology';
import { UploadService } from '@/_api/services/upload';
import { VoucherService } from '@/_api/services/voucher';

import { setApiClientErrorHandler } from '@/_api/lib/client';

export interface ApiInstance {
  adminUser: AdminUserService
  alert: AlertService
  audit: AuditService
  auth: AuthService
  billing: BillingService
  booking: BookingService
  customer: CustomerService
  customerForm: CustomerFormService
  customerMessage: CustomerMessageService
  customerNote: CustomerNoteService
  customerPass: CustomerPassService
  employee: EmployeeService
  integration: IntegrationService
  order: OrderService
  product: ProductService
  reservation: ReservationService
  reservationCustomer: ReservationCustomerService
  resourceType: ResourceTypeService
  settings: SettingsService
  roster: RosterService
  service: ServiceService
  serviceAssociation: ServiceAssociationService
  serviceAutomation: ServiceAutomationService
  serviceCategory: ServiceCategoryService
  serviceForm: ServiceFormService
  serviceResource: ServiceResourceService
  purchaseOrder: PurchaseOrderService
  course: CourseService
  customerCourse: CustomerCourseService
  campaign: CampaignService
  form: FormService
  waitlist: WaitlistService
  contentTemplate: ContentTemplateService
  minisite: MinisiteService
  marketing: MarketingService
  location: LocationService
  emailTemplate: EmailTemplateService
  report: ReportService
  update: UpdateService
  review: ReviewService
  demo: DemoService
  export: ExportService
  referral: ReferralService
  customPage: CustomPageService
  calendarFeed: CalendarFeedService
  stockTake: StockTakeService
  frontDesk: FrontDeskService
  customRole: CustomRoleService
  advancedSettings: AdvancedSettingsService
  chargeModel: ChargeModelService
  customField: CustomFieldService
  discountModel: DiscountModelService
  giftCard: GiftCardService
  pass: PassService
  paymentModel: PaymentModelService
  productBrand: ProductBrandService
  productCategory: ProductCategoryService
  reservationCancellationType: ReservationCancellationTypeService
  reservationStatusType: ReservationStatusTypeService
  role: RoleService
  segment: SegmentService
  supplier: SupplierService
  tax: TaxService
  terminology: TerminologyService
  upload: UploadService
  voucher: VoucherService
}

export default defineNuxtPlugin((nuxtApp) => {
  // Wire up error handlers that require Nuxt context
  setApiClientErrorHandler({
    onAuthenticationError: (exception) => {
      console.log('401 Authentication Error');

      if (exception.logout === true) {
        if ((nuxtApp.$auth as any)?.active) {
          (nuxtApp.$notification as any)?.toast({
            title: (nuxtApp.$i18n as any).t('api.model.authenticationError'),
            text: (nuxtApp.$i18n as any).t('api.model.authenticationErrorInfo'),
            variant: 'danger',
          });
        }

        (nuxtApp.$auth as any)?.logout({ revokeTokens: false });
      }
    },
    onPermissionError: () => {
      console.log('403 Permission Error');
      (nuxtApp.$notification as any)?.toast({
        title: (nuxtApp.$i18n as any).t('api.model.permissionError'),
        text: (nuxtApp.$i18n as any).t('api.model.permissionErrorInfo'),
        variant: 'danger',
      });
    },
    onMaintenanceError: (exception) => {
      console.log('503 Maintenance');
      (nuxtApp.$auth as any)?.setMaintenance?.(exception.data);
    },
  });

  const modules: ApiInstance = {
    adminUser: new AdminUserService(),
    alert: new AlertService(),
    audit: new AuditService(),
    auth: new AuthService(),
    employee: new EmployeeService(),
    resourceType: new ResourceTypeService(),
    roster: new RosterService(),
    service: new ServiceService(),
    serviceAssociation: new ServiceAssociationService(),
    serviceAutomation: new ServiceAutomationService(),
    serviceCategory: new ServiceCategoryService(),
    serviceForm: new ServiceFormService(),
    serviceResource: new ServiceResourceService(),
    customer: new CustomerService(),
    customerForm: new CustomerFormService(),
    customerMessage: new CustomerMessageService(),
    customerNote: new CustomerNoteService(),
    customerPass: new CustomerPassService(),
    reservation: new ReservationService(),
    reservationCustomer: new ReservationCustomerService(),
    order: new OrderService(),
    booking: new BookingService(),
    product: new ProductService(),
    billing: new BillingService(),
    integration: new IntegrationService(),
    settings: new SettingsService(),
    purchaseOrder: new PurchaseOrderService(),
    course: new CourseService(),
    customerCourse: new CustomerCourseService(),
    campaign: new CampaignService(),
    form: new FormService(),
    waitlist: new WaitlistService(),
    contentTemplate: new ContentTemplateService(),
    minisite: new MinisiteService(),
    marketing: new MarketingService(),
    location: new LocationService(),
    emailTemplate: new EmailTemplateService(),
    report: new ReportService(),
    update: new UpdateService(),
    review: new ReviewService(),
    demo: new DemoService(),
    export: new ExportService(),
    referral: new ReferralService(),
    customPage: new CustomPageService(),
    calendarFeed: new CalendarFeedService(),
    stockTake: new StockTakeService(),
    frontDesk: new FrontDeskService(),
    customRole: new CustomRoleService(),
    advancedSettings: new AdvancedSettingsService(),
    chargeModel: new ChargeModelService(),
    customField: new CustomFieldService(),
    discountModel: new DiscountModelService(),
    giftCard: new GiftCardService(),
    pass: new PassService(),
    paymentModel: new PaymentModelService(),
    productBrand: new ProductBrandService(),
    productCategory: new ProductCategoryService(),
    reservationCancellationType: new ReservationCancellationTypeService(),
    reservationStatusType: new ReservationStatusTypeService(),
    role: new RoleService(),
    segment: new SegmentService(),
    supplier: new SupplierService(),
    tax: new TaxService(),
    terminology: new TerminologyService(),
    upload: new UploadService(),
    voucher: new VoucherService(),
  }

  nuxtApp.provide('api', modules)
})
